import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { ArrowUpRight, User, Menu, X } from "lucide-react";

const LINKS = [
  { label: "Workforce", href: "#workforce", id: "workforce" },
  { label: "Capabilities", href: "#capabilities", id: "capabilities" },
  { label: "Live Demo", href: "#demo", id: "demo" },
  { label: "Why Alkyrvis", href: "#why", id: "why" },
  { label: "Pricing", href: "#pricing", id: "pricing" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [visitorName, setVisitorName] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  useEffect(() => {
    const read = () => {
      try {
        const hasToken = !!localStorage.getItem("alkyrvis_visitor_token");
        setVisitorName(hasToken ? localStorage.getItem("alkyrvis_name") || "" : "");
      } catch {
        setVisitorName("");
      }
    };
    read();
    window.addEventListener("visitor-auth-changed", read);
    return () => window.removeEventListener("visitor-auth-changed", read);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "border-b border-white/5 bg-[#050505]/70 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
        <a
          href="#hero"
          className="group flex items-center gap-3"
          data-testid="header-logo-link"
        >
          <img
            src="/assets/emblem.png"
            alt="Alkyrvis emblem"
            className="h-9 w-9 object-contain drop-shadow-[0_0_16px_rgba(216,192,138,0.7)] transition-transform duration-500 group-hover:rotate-[15deg]"
          />
          <span className="font-display text-sm font-medium uppercase tracking-[0.4em] text-[#F5F5F5]">
            Alkyrvis
          </span>
        </a>

        <nav
          className="hidden items-center gap-9 md:flex"
          data-testid="header-nav"
        >
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`header-nav-${l.id}`}
              className="group relative font-mono text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-[width] duration-400 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            type="button"
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-[#F5F5F5] transition-[border-color,color] duration-300 hover:border-gold/40 hover:text-gold md:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          {visitorName ? (
            <button
              type="button"
              data-testid="header-user-chip"
              onClick={() => window.dispatchEvent(new CustomEvent("open-signin"))}
              title={`Signed in as ${visitorName}`}
              className="hidden h-9 w-9 items-center justify-center rounded-full border border-gold/40 bg-gold/15 font-mono text-xs font-medium text-gold transition-[background-color,box-shadow] duration-300 hover:bg-gold/25 hover:shadow-[0_0_20px_rgba(216,192,138,0.3)] sm:flex"
            >
              {visitorName.charAt(0).toUpperCase()}
            </button>
          ) : (
            <button
              type="button"
              data-testid="header-signin-button"
              onClick={() => window.dispatchEvent(new CustomEvent("open-signin"))}
              className="group hidden items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors duration-300 hover:text-gold sm:flex"
            >
              <User className="h-3.5 w-3.5" />
              Sign In
            </button>
          )}
          <a
            href="#start"
            data-testid="header-cta-button"
            className="group hidden items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold transition-[background-color,box-shadow,transform] duration-300 hover:scale-[1.04] hover:bg-gold hover:text-[#050505] hover:shadow-[0_0_30px_rgba(216,192,138,0.4)] active:scale-95 sm:inline-flex"
          >
            Start Building
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[70] flex flex-col bg-[#050505]/95 backdrop-blur-2xl md:hidden"
          >
            <div className="grid-lines absolute inset-0 opacity-40 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_30%,black,transparent)]" />
            <div className="relative flex h-[72px] items-center justify-between px-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold">
                Menu
              </span>
              <button
                type="button"
                data-testid="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-[#F5F5F5] transition-[border-color,color] duration-300 hover:border-gold/40 hover:text-gold"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="relative flex flex-1 flex-col justify-center px-8">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={l.href}
                  data-testid={`mobile-nav-${l.id}`}
                  initial={{ opacity: 0, x: -32 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => setMenuOpen(false)}
                  className="group flex items-center justify-between border-b border-white/5 py-5"
                >
                  <span className="font-display text-3xl font-light tracking-tight text-[#F5F5F5] transition-colors duration-300 group-hover:text-gold">
                    {l.label}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.3em] text-gold/40">
                    0{i + 1}
                  </span>
                </motion.a>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.5 }}
              className="relative flex flex-col gap-3 px-8 pb-12"
            >
              {visitorName ? (
                <p
                  data-testid="mobile-signed-in-note"
                  className="pb-1 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-gold/80"
                >
                  Signed in as {visitorName}
                </p>
              ) : (
                <button
                  type="button"
                  data-testid="mobile-signin-button"
                  onClick={() => {
                    setMenuOpen(false);
                    window.dispatchEvent(new CustomEvent("open-signin"));
                  }}
                  className="flex items-center justify-center gap-2.5 rounded-full border border-white/15 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#F5F5F5] transition-[border-color,color] duration-300 hover:border-gold/40 hover:text-gold"
                >
                  <User className="h-4 w-4" /> Sign In
                </button>
              )}
              <a
                href="#start"
                data-testid="mobile-cta-button"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center gap-2.5 rounded-full bg-gold py-4 font-mono text-xs font-medium uppercase tracking-[0.25em] text-[#050505] shadow-[0_0_40px_rgba(216,192,138,0.35)]"
              >
                Start Building <ArrowUpRight className="h-4 w-4" />
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
